import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { useMaxPhotosPerGallery, useGalleryRefreshInterval } from "../hooks/useSystemSetting";
import {
  useGetGalleryByIdQuery,
  useGetGalleryPhotosQuery,
  useDeletePhotoMutation,
  useDeleteAllGalleryPhotosMutation,
  useGetEventByIdQuery,
  useToggleGalleryRecognitionMutation,
  useToggleGalleryPublicVisibilityMutation
} from "../services/nestjs-base-api";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Image,
  Layout,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Typography,
  Upload,
  message,
  Progress,
  Switch,
  Tooltip
} from "antd";
import {
  InboxOutlined,
  ArrowLeftOutlined,
  PictureOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ClearOutlined,
  DownOutlined,
  UpOutlined,
  FileImageOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  GlobalOutlined,
  LockOutlined,
  LoadingOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { UserRoleEnum } from "../enums/UserRoleEnum";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RcFile, PhotoProcessingStatusEnum } from "../services/nestjs-base-api/types";
import { UploadService } from "../services/uploadService";
import { UpgradeModal } from "../components/UpgradeModal";
import { ProgressiveImage } from "../components/ProgressiveImage";
import styled from "styled-components";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { Dragger } = Upload;

// Define keyframe animation CSS string
const progressBarKeyframes = `
  @keyframes progress-bar-stripes {
    from { background-position: 40px 0; }
    to { background-position: 0 0; }
  }
`;

const StyledContent = styled(Content)`
  margin: 0 24px;
  
  @media (max-width: 768px) {
    margin: 0 16px;
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin: 24px 0 16px 0;
  
  a {
    color: #4169e1;
    text-decoration: none;
    
    &:hover {
      color: #1890ff;
    }
  }
`;

const ContentWrapper = styled.div`
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const GalleryHeaderCard = styled(Card)`
  margin-bottom: 32px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
`;

const StyledTitle = styled(Title)`
  margin: 0 !important;
  color: #1a2847;
  display: inline-flex;
  align-items: center;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
`;

const StyledButton = styled(Button)`
  height: 40px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
`;

const SectionTitle = styled(Title)`
  margin: 0 0 24px 0 !important;
  color: #1a2847;
`;

const PhotoCard = styled(Card)`
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const EmptyStateWrapper = styled.div`
  padding: 60px 0;
  text-align: center;
`;


const LoadingWrapper = styled.div`
  text-align: center;
  padding: 100px 0;
`;

const ProcessingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  z-index: 1;
`;

const DefaultThumbnail = styled.div`
  width: 100%;
  height: 150px;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  
  .anticon {
    font-size: 24px;
    color: #8c8c8c;
    margin-bottom: 8px;
  }
  
  .status-text {
    font-size: 12px;
    color: #8c8c8c;
  }
`;

// Helper function to check if photo is ready for viewing
const isPhotoReady = (photo: any): boolean => {
  // Check explicit status first (backend uses 'status' field)
  if (photo.status !== undefined) {
    return photo.status === PhotoProcessingStatusEnum.Complete;
  }
  
  // Fallback: photo is ready if it has both URLs
  return !!(photo.url && photo.thumbnailUrl);
};

// Helper function to get thumbnail source or default
const getThumbnailSource = (photo: any): string | null => {
  if (isPhotoReady(photo) && photo.thumbnailUrl) {
    return photo.thumbnailUrl;
  }
  return null;
};

// Helper function to get processing status display
const getProcessingStatusDisplay = (photo: any, intl: any) => {
  // Handle explicit processing status (backend uses 'status' field)
  if (photo.status !== undefined) {
    if (photo.status === PhotoProcessingStatusEnum.Complete) {
      return null;
    }
    
    switch (photo.status) {
      case PhotoProcessingStatusEnum.Processing:
        return {
          icon: <LoadingOutlined spin />,
          text: intl.formatMessage({ id: 'gallery.photo.processing' }) || 'Processing...',
          color: '#1890ff'
        };
      case PhotoProcessingStatusEnum.Failed:
        return {
          icon: <ExclamationCircleOutlined />,
          text: intl.formatMessage({ id: 'gallery.photo.failed' }) || 'Failed to process',
          color: '#ff4d4f'
        };
      default:
        return null;
    }
  }
  
  // Fallback: if status is undefined, check URLs
  if (!photo.url || !photo.thumbnailUrl) {
    return {
      icon: <LoadingOutlined spin />,
      text: intl.formatMessage({ id: 'gallery.photo.processing' }) || 'Processing...',
      color: '#1890ff'
    };
  }
  
  return null;
};

// Helper function to check if any photos are currently processing
const hasProcessingPhotos = (photos: any[]): boolean => {
  const processingPhotos = photos?.filter(photo => {
    // Check for explicit processing status first (backend uses 'status' field)
    if (photo.status === PhotoProcessingStatusEnum.Processing) {
      return true;
    }
    
    // Fallback: if status is undefined, check if URLs are missing (indicating processing)
    if (photo.status === undefined) {
      return !photo.url || !photo.thumbnailUrl;
    }
    
    return false;
  }) || [];
  
  // Debug logging removed for performance
  return processingPhotos.length > 0;
};

// Separate PhotoGrid component with aggressive memoization
const PhotoGrid = React.memo(({ 
  photos, 
  intl, 
  isOrgAdmin, 
  isDeleting, 
  handleDeletePhoto 
}: {
  photos: any[];
  intl: any;
  isOrgAdmin: boolean;
  isDeleting: boolean;
  handleDeletePhoto: (id: number) => void;
}) => {
  // Memoize the photo items to prevent recreation
  const photoItems = useMemo(() => {
    return photos.map(photo => {
      const thumbnailSrc = getThumbnailSource(photo);
      const statusDisplay = getProcessingStatusDisplay(photo, intl);
      const photoReady = isPhotoReady(photo);
      
      return (
        <Col xs={24} sm={12} md={8} lg={6} xl={4} key={photo.id}>
          <PhotoCard
            hoverable={photoReady}
            styles={{ body: {padding: 0} }}
            cover={
              <div style={{ position: 'relative' }}>
                {thumbnailSrc ? (
                  <ProgressiveImage
                    thumbnailSrc={thumbnailSrc}
                    fullSrc={photoReady ? photo.url : undefined}
                    alt={photo.filename}
                    style={{ width: '100%', height: 150, objectFit: 'cover' }}
                    placeholderHeight={150}
                    preview={photoReady}
                  />
                ) : (
                  <DefaultThumbnail>
                    <PictureOutlined />
                    <div className="status-text">
                      {intl.formatMessage({ id: 'gallery.photo.placeholder' }) || 'Image unavailable'}
                    </div>
                  </DefaultThumbnail>
                )}
                {statusDisplay && (
                  <ProcessingOverlay>
                    <div style={{ color: statusDisplay.color, fontSize: '16px' }}>
                      {statusDisplay.icon}
                    </div>
                    <div style={{ color: statusDisplay.color, fontSize: '12px', marginTop: 4 }}>
                      {statusDisplay.text}
                    </div>
                  </ProcessingOverlay>
                )}
              </div>
            }
            actions={isOrgAdmin ? [
              <Popconfirm
                key="delete"
                title={intl.formatMessage({ id: 'gallery.deletePhotoConfirm' }) || 'Are you sure you want to delete this photo?'}
                onConfirm={() => handleDeletePhoto(photo.id)}
                okText={intl.formatMessage({ id: 'common.yesButton' }) || 'Yes'}
                cancelText={intl.formatMessage({ id: 'common.noButton' }) || 'No'}
                okButtonProps={{ danger: true }}
                disabled={isDeleting}
              >
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  loading={isDeleting}
                >
                  {intl.formatMessage({ id: 'gallery.deletePhoto' }) || 'Delete Photo'}
                </Button>
              </Popconfirm>
            ] : []}
          >
            <div style={{ padding: 8 }}>
              <Text ellipsis style={{ width: '100%', display: 'block', color: '#1a2847', fontWeight: 500 }}>
                {photo.filename}
              </Text>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {(photo.fileSize / 1024).toFixed(0)} KB
                </Text>
                {statusDisplay && (
                  <Text style={{ fontSize: '10px', color: statusDisplay.color }}>
                    {statusDisplay.text}
                  </Text>
                )}
              </div>
            </div>
          </PhotoCard>
        </Col>
      );
    });
  }, [photos, intl, isOrgAdmin, isDeleting, handleDeletePhoto]);

  return (
    <div>
      <Image.PreviewGroup>
        <Row gutter={[24, 24]}>
          {photoItems}
        </Row>
      </Image.PreviewGroup>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if photos array actually changes (not just reference)
  if (prevProps.photos.length !== nextProps.photos.length) return false;
  
  // Check if any photo has actually changed
  for (let i = 0; i < prevProps.photos.length; i++) {
    const prev = prevProps.photos[i];
    const next = nextProps.photos[i];
    
    if (
      prev.id !== next.id ||
      prev.status !== next.status ||
      prev.url !== next.url ||
      prev.thumbnailUrl !== next.thumbnailUrl
    ) {
      return false; // Re-render needed
    }
  }
  
  // Check other props
  return (
    prevProps.isOrgAdmin === nextProps.isOrgAdmin &&
    prevProps.isDeleting === nextProps.isDeleting
  );
});

const RecognitionToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f0f5ff;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const GalleryDetailPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { galleryId, eventId: eventIdParam } = useParams<{ galleryId: string, eventId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL format and old format with query parameters
  const eventId = eventIdParam || searchParams.get('eventId');
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isDeleteAllModalVisible, setIsDeleteAllModalVisible] = useState(false);
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<RcFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileUploadProgress, setFileUploadProgress] = useState<Map<string, number>>(new Map());
  const [failedUploads, setFailedUploads] = useState<Set<string>>(new Set());
  const [isFileListCollapsed, setIsFileListCollapsed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  
  // Smart refresh state
  const [isSmartRefreshing, setIsSmartRefreshing] = useState(false);
  const [refreshIntervalId, setRefreshIntervalId] = useState<NodeJS.Timeout | null>(null);
  
  // Get max photos per gallery limit from system settings
  const maxPhotosPerGallery = useMaxPhotosPerGallery("1000");
  
  // Get smart refresh configuration from system settings
  const galleryRefreshInterval = useGalleryRefreshInterval('3000');
  
  // Check if user is org admin
  const isOrgAdmin = user?.roles?.includes(UserRoleEnum.OrganisationAdmin) ||
    user?.roles?.includes(UserRoleEnum.SuperAdmin);

  // Get gallery details
  const {
    data: gallery,
    error: galleryError,
    isLoading: isLoadingGallery,
    refetch: refetchGallery
  } = useGetGalleryByIdQuery({
    jwtToken: user.jwtToken,
    galleryId: Number(galleryId)
  }, { skip: !galleryId });

  // Get gallery photos
  const {
    data: photos,
    error: photosError,
    isLoading: isLoadingPhotos,
    refetch: refetchPhotos
  } = useGetGalleryPhotosQuery({
    jwtToken: user.jwtToken,
    galleryId: Number(galleryId)
  }, { skip: !galleryId });
  
  // Get event details if eventId is available
  const {
    data: event,
    error: eventError
  } = useGetEventByIdQuery({
    jwtToken: user.jwtToken,
    eventId: Number(eventId)
  }, { skip: !eventId });

  // Upload photos mutation - removed as we're using axios service directly
  
  // Delete photo mutation
  const [deletePhoto, { isLoading: isDeleting }] = useDeletePhotoMutation();
  
  // Delete all photos mutation
  const [deleteAllPhotos, { isLoading: isDeletingAll }] = useDeleteAllGalleryPhotosMutation();
  
  // Toggle gallery recognition mutation
  const [toggleGalleryRecognition, { isLoading: isTogglingRecognition }] = useToggleGalleryRecognitionMutation();
  
  // Toggle gallery public visibility mutation
  const [toggleGalleryPublicVisibility, { isLoading: isTogglingVisibility }] = useToggleGalleryPublicVisibilityMutation();

  // Smart refresh functions
  const stopSmartRefresh = useCallback(() => {
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
      setRefreshIntervalId(null);
    }
    setIsSmartRefreshing(false);
  }, [refreshIntervalId]);

  const startSmartRefresh = useCallback(() => {
    if (isSmartRefreshing || refreshIntervalId) {
      return;
    }
    
    setIsSmartRefreshing(true);
    
    // Start immediately refreshing, then set up interval
    refetchPhotos();
    
    const interval = setInterval(() => {
      refetchPhotos();
    }, parseInt(galleryRefreshInterval, 10));
    setRefreshIntervalId(interval);
  }, [isSmartRefreshing, refreshIntervalId, refetchPhotos, galleryRefreshInterval]);

  // Monitor processing status to automatically stop refreshing when done
  useEffect(() => {
    if (!isSmartRefreshing || !photos) return;

    // Check if any photos are still processing
    const stillProcessing = hasProcessingPhotos(photos);
    
    if (!stillProcessing) {
      // All photos are done processing (either complete or failed)
      stopSmartRefresh();
      
      const completedCount = photos.filter(photo => {
        // Check explicit status first (backend uses 'status' field)
        if (photo.status !== undefined) {
          return photo.status === PhotoProcessingStatusEnum.Complete;
        }
        // Fallback: photo is complete if it has both URLs
        return !!(photo.url && photo.thumbnailUrl);
      }).length;
      
      const failedCount = photos.filter(photo => {
        // Only count as failed if we have explicit failed status
        return photo.status === PhotoProcessingStatusEnum.Failed;
      }).length;
      
      if (completedCount > 0) {
        if (failedCount > 0) {
          messageApi.success(
            `Processing complete! ${completedCount} photos ready, ${failedCount} failed.`
          );
        } else {
          messageApi.success(
            `Processing complete! All ${completedCount} photos are ready.`
          );
        }
      }
    }
  }, [photos, isSmartRefreshing, stopSmartRefresh, messageApi]);

  // Auto-start smart refresh if photos are processing when component loads
  useEffect(() => {
    if (!photos || isSmartRefreshing) return;
    
    // If there are processing photos and we're not already refreshing, start refresh
    if (hasProcessingPhotos(photos)) {
      startSmartRefresh();
    }
  }, [photos, isSmartRefreshing, startSmartRefresh]);

  // Cleanup refresh interval on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
      }
    };
  }, [refreshIntervalId]);

  // Handle file upload
  const handleUpload = useCallback(async () => {
    if (uploadedFiles.length === 0 || !galleryId) return;

    setIsUploading(true);
    setUploadProgress(0);
    setFileUploadProgress(new Map());
    setFailedUploads(new Set());

    try {
      // Use the new upload service with real progress tracking
      const result = await UploadService.uploadPhotosWithProgress({
        jwtToken: user.jwtToken,
        galleryId,
        files: uploadedFiles,
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
        onFileProgress: (fileName, progress) => {
          setFileUploadProgress(prev => {
            const newMap = new Map(prev);
            newMap.set(fileName, progress);
            return newMap;
          });
        }
      });

      // Small delay to show 100% before closing the modal
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Track failed uploads for visual indication
      const failedFileNames = new Set(result.failed.map(f => f.fileName));
      setFailedUploads(failedFileNames);
      
      // Check if any uploads failed
      if (result.failed.length > 0) {
        // Some files failed
        const successCount = result.successful.length;
        const failedCount = result.failed.length;
        
        if (successCount === 0) {
          // All uploads failed
          messageApi.error(
            intl.formatMessage({ id: 'gallery.allUploadsFailed' }) || 
            'All uploads failed. Please try again.'
          );
        } else {
          // Partial success
          messageApi.warning(
            intl.formatMessage(
              { id: 'gallery.partialUploadSuccess' },
              { success: successCount, failed: failedCount }
            ) || 
            `${successCount} photos uploaded successfully, ${failedCount} failed.`
          );
        }
        
        // Show detailed error modal
        Modal.error({
          title: intl.formatMessage({ id: 'gallery.uploadFailures' }) || 'Upload Failures',
          content: (
            <div>
              <p>{intl.formatMessage({ id: 'gallery.failedUploads' }) || 'The following files failed to upload:'}</p>
              <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {result.failed.map((failure, index) => (
                  <li key={index}>
                    <strong>{failure.fileName}:</strong> {failure.error}
                  </li>
                ))}
              </ul>
            </div>
          ),
          okText: intl.formatMessage({ id: 'common.ok' }) || 'OK'
        });
      } else {
        // All uploads succeeded
        messageApi.success(
          intl.formatMessage({ id: 'gallery.uploadSuccess' }) || 
          'Photos uploaded successfully'
        );
      }
      
      // Only close modal if some uploads succeeded
      if (result.successful.length > 0) {
        setIsUploadModalVisible(false);
        setUploadedFiles([]);
        setUploadProgress(0);
        setFileUploadProgress(new Map());
        setIsFileListCollapsed(false);
        
        // Immediately refetch photos to get the latest with processing status
        try {
          const updatedPhotosResult = await refetchPhotos();
          const updatedPhotos = updatedPhotosResult.data;
          
          // Start smart refresh only if there are photos that are still processing
          if (updatedPhotos && hasProcessingPhotos(updatedPhotos)) {
            startSmartRefresh();
          }
        } catch (error) {
          // Fallback: start smart refresh anyway since upload was successful
          startSmartRefresh();
        }
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        logout();
      } else {
        messageApi.error(intl.formatMessage({ id: 'gallery.uploadError' }) || 'Error uploading photos');
      }
    } finally {
      setIsUploading(false);
    }
  }, [uploadedFiles, galleryId, user.jwtToken, intl, logout, messageApi, refetchPhotos, startSmartRefresh]);

  // Check if user can upload more photos
  const canUploadMorePhotos = useCallback(() => {
    if (!photos) return true; // If photos data isn't loaded yet, allow upload
    
    const maxPhotosCount = parseInt(maxPhotosPerGallery, 10) || 10;
    return photos.length < maxPhotosCount;
  }, [photos, maxPhotosPerGallery]);
  
  // Get remaining photo quota
  const getRemainingPhotoQuota = useCallback(() => {
    if (!photos) return parseInt(maxPhotosPerGallery, 10) || 10;
    
    const maxPhotosCount = parseInt(maxPhotosPerGallery, 10) || 10;
    return Math.max(0, maxPhotosCount - photos.length);
  }, [photos, maxPhotosPerGallery]);
  
  // Show upload modal or upgrade modal
  const showUploadModal = () => {
    if (canUploadMorePhotos()) {
      setUploadedFiles([]);
      setUploadProgress(0);
      setIsFileListCollapsed(false);
      setIsUploadModalVisible(true);
    } else {
      setIsUpgradeModalVisible(true);
    }
  };

  // Handle cancel modal
  const handleCancelUpload = () => {
    setIsUploadModalVisible(false);
    setUploadedFiles([]);
    setUploadProgress(0);
    setFileUploadProgress(new Map());
    setFailedUploads(new Set());
    setIsFileListCollapsed(false);
  };
  
  // Handle close upgrade modal
  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalVisible(false);
  };
  
  // Handle photo deletion
  const handleDeletePhoto = useCallback(async (photoId: number) => {
    try {
      // Stop any ongoing smart refresh
      stopSmartRefresh();
      
      await deletePhoto({
        jwtToken: user.jwtToken,
        photoId
      }).unwrap();
      
      messageApi.success(intl.formatMessage({ id: 'gallery.deletePhotoSuccess' }) || 'Photo deleted successfully');
      refetchPhotos();
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        logout();
      } else {
        messageApi.error(intl.formatMessage({ id: 'gallery.deletePhotoError' }) || 'Error deleting photo');
      }
    }
  }, [deletePhoto, user.jwtToken, intl, logout, messageApi, refetchPhotos, stopSmartRefresh]);
  
  // Handle deletion of all photos
  const handleDeleteAllPhotos = useCallback(async () => {
    if (!gallery) return;
    
    try {
      // Stop any ongoing smart refresh
      stopSmartRefresh();
      
      await deleteAllPhotos({
        jwtToken: user.jwtToken,
        galleryHash: gallery.hash,
      }).unwrap();
      
      messageApi.success(intl.formatMessage({ id: 'gallery.deleteAllPhotosSuccess' }) || 'All photos deleted successfully');
      setIsDeleteAllModalVisible(false);
      refetchPhotos();
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        logout();
      } else {
        messageApi.error(intl.formatMessage({ id: 'gallery.deleteAllPhotosError' }) || 'Error deleting photos');
      }
    }
  }, [deleteAllPhotos, gallery, user.jwtToken, intl, logout, messageApi, refetchPhotos, stopSmartRefresh]);
  
  // Handle toggling facial recognition
  const handleToggleRecognition = useCallback(async (exclude: boolean) => {
    if (!gallery || !galleryId) return;
    
    Modal.confirm({
      title: exclude 
        ? intl.formatMessage({ id: 'gallery.excludeRecognitionTitle' }) || 'Disable Facial Recognition?'
        : intl.formatMessage({ id: 'gallery.includeRecognitionTitle' }) || 'Enable Facial Recognition?',
      content: exclude
        ? intl.formatMessage({ id: 'gallery.excludeRecognitionMessage' }) || 'Excluding this gallery means guests won\'t be able to find photos from this gallery when they upload selfies. Are you sure?'
        : intl.formatMessage({ id: 'gallery.includeRecognitionMessage' }) || 'This will allow guests to find photos from this gallery when they upload selfies. Continue?',
      icon: exclude ? <EyeInvisibleOutlined /> : <EyeOutlined />,
      okText: intl.formatMessage({ id: 'common.yesButton' }) || 'Yes',
      cancelText: intl.formatMessage({ id: 'common.noButton' }) || 'No',
      onOk: async () => {
        try {
          await toggleGalleryRecognition({
            jwtToken: user.jwtToken,
            galleryId: parseInt(galleryId),
            exclude
          }).unwrap();
          
          messageApi.success(
            exclude 
              ? intl.formatMessage({ id: 'gallery.recognitionDisabled' }) || 'Facial recognition disabled for this gallery'
              : intl.formatMessage({ id: 'gallery.recognitionEnabled' }) || 'Facial recognition enabled for this gallery'
          );
          
          // Refetch gallery data to update the UI
          refetchGallery();
        } catch (error) {
          if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
            logout();
          } else {
            messageApi.error(intl.formatMessage({ id: 'gallery.toggleRecognitionError' }) || 'Error updating facial recognition settings');
          }
        }
      }
    });
  }, [gallery, galleryId, toggleGalleryRecognition, user.jwtToken, intl, messageApi, refetchGallery, logout]);
  
  // Handle toggling public visibility
  const handleTogglePublicVisibility = useCallback(async (publiclyVisible: boolean) => {
    if (!gallery || !galleryId) return;
    
    Modal.confirm({
      title: publiclyVisible 
        ? intl.formatMessage({ id: 'gallery.makePublicTitle' }) || 'Make Gallery Public?'
        : intl.formatMessage({ id: 'gallery.makePrivateTitle' }) || 'Make Gallery Private?',
      content: publiclyVisible
        ? intl.formatMessage({ id: 'gallery.makePublicMessage' }) || 'This will make the gallery publicly visible to anyone with the link. Continue?'
        : intl.formatMessage({ id: 'gallery.makePrivateMessage' }) || 'This will make the gallery private and only accessible to authenticated users. Continue?',
      icon: publiclyVisible ? <GlobalOutlined /> : <LockOutlined />,
      okText: intl.formatMessage({ id: 'common.yesButton' }) || 'Yes',
      cancelText: intl.formatMessage({ id: 'common.noButton' }) || 'No',
      onOk: async () => {
        try {
          await toggleGalleryPublicVisibility({
            jwtToken: user.jwtToken,
            galleryId: parseInt(galleryId),
            publiclyVisible
          }).unwrap();
          
          messageApi.success(
            publiclyVisible 
              ? intl.formatMessage({ id: 'gallery.madePublic' }) || 'Gallery is now publicly visible'
              : intl.formatMessage({ id: 'gallery.madePrivate' }) || 'Gallery is now private'
          );
          
          // Refetch gallery data to update the UI
          refetchGallery();
        } catch (error) {
          if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
            logout();
          } else {
            messageApi.error(intl.formatMessage({ id: 'gallery.toggleVisibilityError' }) || 'Error updating gallery visibility');
          }
        }
      }
    });
  }, [gallery, galleryId, toggleGalleryPublicVisibility, user.jwtToken, intl, messageApi, refetchGallery, logout]);
  
  // Show delete all confirmation modal
  const showDeleteAllModal = () => {
    setIsDeleteAllModalVisible(true);
  };
  
  // Handle cancel delete all modal
  const handleCancelDeleteAll = () => {
    setIsDeleteAllModalVisible(false);
  };
  
      // Handle download all photos
  const handleDownloadAllPhotos = useCallback(async () => {
    if (!gallery || !photos || photos.length === 0) return;
    
    // Check if there are any completed photos to download
    const completedPhotos = photos.filter(photo => isPhotoReady(photo));
    if (completedPhotos.length === 0) {
      messageApi.warning(intl.formatMessage({ id: 'gallery.noCompletedPhotos' }) || 'No completed photos to download');
      return;
    }
    
    const key = 'downloadAllProgress';
    setIsDownloadingAll(true);
    messageApi.loading({ content: intl.formatMessage({ id: 'gallery.preparingDownload' }) || 'Preparing download...', key, duration: 0 });
    
    try {
      // Get API base URL
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const apiBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      
      let downloadUrl: string;
      let response: Response;
      
      if (user?.jwtToken) {
        // Authenticated user - use the authenticated endpoint
        downloadUrl = `${apiBaseUrl}/api/galleries/${galleryId}/download-all`;
        response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.jwtToken}`
          }
        });
      } else if (gallery.isPubliclyVisible) {
        // Public access - first generate a download token
        const tokenUrl = `${apiBaseUrl}/api/galleries/hash/${gallery.hash}/download-token`;
        const tokenResponse = await fetch(tokenUrl, {
          method: 'POST'
        });
        
        if (!tokenResponse.ok) {
          throw new Error('Failed to generate download token');
        }
        
        const { token } = await tokenResponse.json();
        
        // Use the public endpoint with the token
        downloadUrl = `${apiBaseUrl}/api/galleries/hash/${gallery.hash}/download-all?token=${token}`;
        response = await fetch(downloadUrl);
      } else {
        throw new Error('Gallery is not publicly accessible');
      }
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(intl.formatMessage({ id: 'gallery.noPhotosToDownload' }) || 'No photos available for download');
        } else if (response.status === 401) {
          throw new Error(intl.formatMessage({ id: 'gallery.unauthorized' }) || 'Unauthorized access');
        }
        throw new Error(intl.formatMessage({ id: 'gallery.downloadFailed' }) || 'Download failed');
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `${gallery.name}_photos.zip`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(link.href);
      
      messageApi.success({ 
        content: intl.formatMessage(
          { id: 'gallery.downloadSuccess' }, 
          { count: completedPhotos.length }
        ) || `Successfully downloaded ${completedPhotos.length} photos!`, 
        key 
      });
    } catch (error: any) {
      console.error('Error downloading gallery photos:', error);
      
      if (error.message.includes('Unauthorized')) {
        messageApi.error({ 
          content: intl.formatMessage({ id: 'gallery.unauthorizedDownload' }) || 'You are not authorized to download these photos', 
          key 
        });
      } else {
        messageApi.error({ 
          content: error.message || intl.formatMessage({ id: 'gallery.downloadError' }) || 'Failed to download photos. Please try again.', 
          key 
        });
      }
    } finally {
      setIsDownloadingAll(false);
    }
  }, [gallery, galleryId, photos, user, intl, messageApi]);
  
  // Toggle file list collapse state
  const toggleFileListCollapse = () => {
    setIsFileListCollapsed(!isFileListCollapsed);
  };
  
  // Calculate total file size
  const calculateTotalFileSize = useCallback(() => {
    return uploadedFiles.reduce((total, file) => total + file.size, 0);
  }, [uploadedFiles]);

  // Handle file selection before upload
  const handleFileChange = useCallback(({ fileList }: { fileList: any[] }) => {
    const remainingQuota = getRemainingPhotoQuota();
    
    // Enforce file count limit based on remaining quota
    let filesToUpload = fileList;
    if (fileList.length > remainingQuota) {
      filesToUpload = fileList.slice(0, remainingQuota);
      
      // Show warning if files were truncated
      if (fileList.length !== filesToUpload.length) {
        messageApi.warning(
          intl.formatMessage(
            { id: 'gallery.uploadLimitExceededHint' },
            { limit: maxPhotosPerGallery }
          ) || `You can upload up to ${maxPhotosPerGallery} photos in this gallery.`
        );
      }
    }
    
    // Convert to RcFile type with uid, etc.
    const processedFiles = filesToUpload.map(file => file.originFileObj);
    setUploadedFiles(processedFiles);
    
    // Auto-collapse file list if more than 5 files are selected
    if (processedFiles.length > 1) {
      setIsFileListCollapsed(true);
    } else {
      setIsFileListCollapsed(false);
    }
  }, [getRemainingPhotoQuota, intl, maxPhotosPerGallery, messageApi]);

  // Custom validation for file type, size, and quota
  const beforeUpload = useCallback((file: RcFile) => {
    const isImage = /^image\/(jpeg|png|jpg)$/.test(file.type);
    if (!isImage) {
      messageApi.error(intl.formatMessage({ id: 'gallery.uploadTypeError' }) || 'You can only upload image files');
      return Upload.LIST_IGNORE;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 10;
    if (!isLt5M) {
      messageApi.error(intl.formatMessage({ id: 'gallery.uploadSizeError' }) || 'Image must be smaller than 10MB');
      return Upload.LIST_IGNORE;
    }
    
    // Check if this file would exceed the quota
    const remainingQuota = getRemainingPhotoQuota();
    if (uploadedFiles.length >= remainingQuota) {
      messageApi.error(
        intl.formatMessage(
          { id: 'gallery.uploadLimitExceededHint' },
          { limit: maxPhotosPerGallery }
        ) || `You can upload up to ${maxPhotosPerGallery} photos in this gallery.`
      );
      return Upload.LIST_IGNORE;
    }
    
    return true;
  }, [intl, messageApi, uploadedFiles.length, getRemainingPhotoQuota, maxPhotosPerGallery]);

  // Navigate back to event details
  const goBackToEvent = useCallback(() => {
    if (eventId) {
      navigate(`/dashboard/events/${eventId}`);
    } else {
      navigate('/dashboard/events');
    }
  }, [navigate, eventId]);

  // Check for errors
  useEffect(() => {
    if (galleryError || photosError || eventError) {
      if (
        (galleryError && 'status' in galleryError && galleryError.status === 401) ||
        (photosError && 'status' in photosError && photosError.status === 401) ||
        (eventError && 'status' in eventError && eventError.status === 401)
      ) {
        logout();
      }
    }
  }, [galleryError, photosError, eventError, logout]);
  
  // Add and remove progress bar animation style
  useEffect(() => {
    // Create and append style element
    const styleElement = document.createElement('style');
    styleElement.innerHTML = progressBarKeyframes;
    document.head.appendChild(styleElement);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Loading state
  if (isLoadingGallery) {
    return (
      <StyledContent>
        <LoadingWrapper>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16, color: '#666' }}>
            {intl.formatMessage({ id: 'gallery.detailLoading' }) || 'Loading gallery details...'}
          </Paragraph>
        </LoadingWrapper>
      </StyledContent>
    );
  }

  // Gallery not found
  if (!gallery && !isLoadingGallery) {
    return (
      <StyledContent>
        <ContentWrapper>
          <EmptyStateWrapper>
            <Empty
              description={intl.formatMessage({ id: 'gallery.notFound' }) || 'Gallery not found'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <StyledButton type="primary" onClick={goBackToEvent} style={{ marginTop: 16 }}>
              {intl.formatMessage({ id: 'gallery.backToEvent' }) || 'Back to Event'}
            </StyledButton>
          </EmptyStateWrapper>
        </ContentWrapper>
      </StyledContent>
    );
  }

  return (
    <StyledContent>
      {contextHolder}
      <StyledBreadcrumb>
        <Breadcrumb.Item>{user.organisation ? user.organisation.name : intl.formatMessage({ id: 'common.platformName' })}</Breadcrumb.Item>
        <Breadcrumb.Item>
          <a onClick={goBackToEvent}><FormattedMessage id={'section.eventsName'} /></a>
        </Breadcrumb.Item>
        {eventId && (
          <Breadcrumb.Item>
            <a onClick={goBackToEvent} style={{ maxWidth: '200px', display: 'inline-block' }}>
              <Text ellipsis={{ tooltip: event?.name }}>
                {event?.name ? (event.name.length > 20 ? `${event.name.substring(0, 20)}...` : event.name) : (intl.formatMessage({ id: 'gallery.event' }) || 'Event')}
              </Text>
            </a>
          </Breadcrumb.Item>
        )}
        <Breadcrumb.Item>{gallery?.name}</Breadcrumb.Item>
      </StyledBreadcrumb>

      <ContentWrapper>
        {/* Gallery Header */}
        <GalleryHeaderCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <StyledButton 
                  icon={<ArrowLeftOutlined />} 
                  onClick={goBackToEvent}
                  style={{ marginBottom: 16, alignSelf: 'flex-start' }}
                >
                  {intl.formatMessage({ id: 'gallery.backToEvent' }) || 'Back to Event'}
                </StyledButton>
                
                <StyledTitle level={2} style={{ marginTop: 0 }}>
                  {gallery?.name}
                  {gallery?.excludeFromRecognition && (
                    <Tooltip title={intl.formatMessage({ id: 'gallery.recognitionDisabledTooltip' }) || 'Guests cannot find photos from this gallery using selfies'}>
                      <EyeInvisibleOutlined 
                        style={{ 
                          marginLeft: 12, 
                          fontSize: '0.7em', 
                          color: '#ff7875',
                          verticalAlign: 'middle'
                        }} 
                      />
                    </Tooltip>
                  )}
                  {gallery?.isPubliclyVisible && (
                    <Tooltip title={intl.formatMessage({ id: 'gallery.publiclyVisibleTooltip' }) || 'This gallery is visible to anyone with the link'}>
                      <GlobalOutlined 
                        style={{ 
                          marginLeft: 12, 
                          fontSize: '0.7em', 
                          color: '#52c41a',
                          verticalAlign: 'middle'
                        }} 
                      />
                    </Tooltip>
                  )}
                </StyledTitle>
              </div>
              
              <Space direction="vertical" size={12} style={{ marginTop: 16, marginBottom: 16, width: '100%' }}>
                <InfoItem>
                  <PictureOutlined />
                  <Text style={{ color: '#666' }}>
                    {intl.formatMessage(
                      { id: 'gallery.photosCount' }, 
                      { count: photos?.length || 0 }
                    ) || `${photos?.length || 0} Photos`}
                  </Text>
                </InfoItem>
                
                {gallery?.type && (
                  <InfoItem>
                    <Text style={{ color: '#666' }}>
                      {intl.formatMessage({ id: 'gallery.form.type' }) || 'Type'}:{' '}
                      {intl.formatMessage({ id: `gallery.type.${gallery.type.toLowerCase()}` }) || gallery.type}
                    </Text>
                  </InfoItem>
                )}
              </Space>
              
              {gallery?.description && (
                <Paragraph style={{ color: '#666', marginBottom: 20 }}>
                  {gallery.description}
                </Paragraph>
              )}
            </div>
            
            {/* Download button for non-admin users */}
            {!isOrgAdmin && photos && photos.length > 0 && photos.some(photo => isPhotoReady(photo)) && (
              <div>
                <StyledButton
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadAllPhotos}
                  disabled={isDownloadingAll}
                  type="primary"
                >
                  {intl.formatMessage({ id: 'gallery.downloadAllPhotos' }) || 'Download All Photos'}
                </StyledButton>
              </div>
            )}
            
            {isOrgAdmin && (
              <div>
                {/* Facial Recognition Toggle */}
                <RecognitionToggle>
                  <Tooltip 
                    title={
                      gallery?.excludeFromRecognition 
                        ? intl.formatMessage({ id: 'gallery.recognitionDisabledTooltip' }) || 'Guests cannot find photos from this gallery using selfies'
                        : intl.formatMessage({ id: 'gallery.recognitionEnabledTooltip' }) || 'Guests can find photos from this gallery using selfies'
                    }
                  >
                    <Space align="center">
                      {gallery?.excludeFromRecognition ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      <Text strong>{intl.formatMessage({ id: 'gallery.facialRecognition' }) || 'Facial Recognition'}</Text>
                      <Switch
                        checked={!gallery?.excludeFromRecognition}
                        onChange={(checked) => handleToggleRecognition(!checked)}
                        loading={isTogglingRecognition}
                        checkedChildren={<EyeOutlined />}
                        unCheckedChildren={<EyeInvisibleOutlined />}
                      />
                    </Space>
                  </Tooltip>
                  {gallery?.excludeFromRecognition && (
                    <Text type="warning" style={{ fontSize: '12px', marginLeft: 'auto' }}>
                      {intl.formatMessage({ id: 'gallery.notSearchableByGuests' }) || 'Not searchable by guests'}
                    </Text>
                  )}
                </RecognitionToggle>
                
                {/* Public Visibility Toggle */}
                <RecognitionToggle>
                  <Tooltip 
                    title={
                      gallery?.isPubliclyVisible
                        ? intl.formatMessage({ id: 'gallery.publiclyVisibleTooltip' }) || 'This gallery is visible to anyone with the link'
                        : intl.formatMessage({ id: 'gallery.privateTooltip' }) || 'This gallery is only visible to authenticated users'
                    }
                  >
                    <Space align="center">
                      {gallery?.isPubliclyVisible ? <GlobalOutlined /> : <LockOutlined />}
                      <Text strong>{intl.formatMessage({ id: 'gallery.publiclyVisible' }) || 'Publicly Visible'}</Text>
                      <Switch
                        checked={gallery?.isPubliclyVisible || false}
                        onChange={(checked) => handleTogglePublicVisibility(checked)}
                        loading={isTogglingVisibility}
                        checkedChildren={<GlobalOutlined />}
                        unCheckedChildren={<LockOutlined />}
                      />
                    </Space>
                  </Tooltip>
                  {!gallery?.isPubliclyVisible && (
                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: 'auto' }}>
                      {intl.formatMessage({ id: 'gallery.privateAccess' }) || 'Private access only'}
                    </Text>
                  )}
                </RecognitionToggle>
                
                {/* Action Buttons */}
                <Space wrap>
                  {photos && photos.length > 0 && (
                    <>
                      <StyledButton
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadAllPhotos}
                        disabled={isDownloadingAll || !photos.some(photo => isPhotoReady(photo))}
                      >
                        {intl.formatMessage({ id: 'gallery.downloadAllPhotos' }) || 'Download All Photos'}
                      </StyledButton>
                      <StyledButton
                        danger
                        icon={<ClearOutlined />}
                        onClick={showDeleteAllModal}
                        disabled={isDeletingAll || isUploading}
                      >
                        {intl.formatMessage({ id: 'gallery.deleteAllPhotos' }) || 'Remove All Photos'}
                      </StyledButton>
                    </>
                  )}
                  <StyledButton
                    type="primary"
                    icon={<CloudUploadOutlined />}
                    onClick={showUploadModal}
                    disabled={isUploading || isDeletingAll}
                  >
                    {intl.formatMessage({ id: 'gallery.uploadPhotos' }) || 'Upload Photos'}
                  </StyledButton>
                </Space>
              </div>
            )}
          </div>
        </GalleryHeaderCard>

        {/* Photos Grid */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <SectionTitle level={3} style={{ margin: 0 }}>
              {intl.formatMessage({ id: 'gallery.photos' }) || 'Photos'}
            </SectionTitle>
            {isSmartRefreshing && photos && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1890ff' }}>
                <Spin size="small" />
                <Text style={{ fontSize: '14px', color: '#1890ff' }}>
                  {(() => {
                    const processingCount = photos.filter(photo => {
                      // Check explicit status first (backend uses 'status' field)
                      if (photo.status !== undefined) {
                        return photo.status === PhotoProcessingStatusEnum.Processing;
                      }
                      // Fallback: check if URLs are missing
                      return !photo.url || !photo.thumbnailUrl;
                    }).length;
                    return processingCount > 0 
                      ? `Processing ${processingCount} photo${processingCount === 1 ? '' : 's'}...`
                      : 'Processing uploads...';
                  })()}
                </Text>
              </div>
            )}
          </div>
          
          {isLoadingPhotos ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: 16, color: '#666' }}>
                {intl.formatMessage({ id: 'gallery.loadingPhotos' }) || 'Loading photos...'}
              </Paragraph>
            </div>
          ) : photos && photos.length > 0 ? (
            <PhotoGrid 
              photos={photos} 
              intl={intl} 
              isOrgAdmin={isOrgAdmin} 
              isDeleting={isDeleting} 
              handleDeletePhoto={handleDeletePhoto} 
            />
          ) : (
            <EmptyStateWrapper>
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    {intl.formatMessage({ id: 'gallery.noPhotos' }) || 'No photos found'}
                    {isOrgAdmin && (
                      <div style={{ marginTop: 16 }}>
                        <StyledButton 
                          type="primary" 
                          icon={<CloudUploadOutlined />} 
                          onClick={showUploadModal}
                        >
                          {intl.formatMessage({ id: 'gallery.uploadPhotos' }) || 'Upload Photos'}
                        </StyledButton>
                      </div>
                    )}
                  </span>
                }
              />
            </EmptyStateWrapper>
          )}
        </div>
      </ContentWrapper>

      {/* Upload Photos Modal */}
      <Modal
        title={intl.formatMessage({ id: 'gallery.uploadTitle' }) || 'Upload Photos'}
        open={isUploadModalVisible}
        onCancel={handleCancelUpload}
        destroyOnClose={true}
        width={600}
        footer={[
          <Button key="back" onClick={handleCancelUpload} disabled={isUploading}>
            {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
          </Button>,
          <StyledButton 
            key="submit" 
            type="primary" 
            onClick={handleUpload} 
            loading={isUploading}
            disabled={uploadedFiles.length === 0}
          >
            {isUploading 
              ? `${uploadProgress}%` 
              : intl.formatMessage({ id: 'gallery.upload' }) || 'Upload'}
          </StyledButton>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>
              {intl.formatMessage(
                { id: 'gallery.uploadRemainingLimit' },
                { 
                  remaining: getRemainingPhotoQuota(), 
                  total: maxPhotosPerGallery 
                }
              ) || `${getRemainingPhotoQuota()} of ${maxPhotosPerGallery} photos remaining`}
            </Text>
            {uploadedFiles.length > 0 && (
              <Text type="secondary">
                {uploadedFiles.length} {intl.formatMessage({ id: 'gallery.photos' }) || 'Photos'} {intl.formatMessage({ id: 'gallery.selected' }) || 'selected'}
              </Text>
            )}
          </div>
          {/* Progress bar to show quota usage */}
          <div style={{ 
            width: '100%', 
            height: 8, 
            backgroundColor: '#f0f0f0', 
            borderRadius: 4, 
            marginTop: 8,
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: photos ? `${(photos.length / parseInt(maxPhotosPerGallery, 10)) * 100}%` : '0%', 
              height: '100%', 
              backgroundColor: '#1890ff',
              borderRadius: 4,
              transition: 'width 0.3s'
            }}></div>
          </div>
        </div>
        
        {/* File summary and toggle button (shown when files are selected) */}
        {uploadedFiles.length > 0 && (
          <Card 
            style={{ marginBottom: 16 }} 
            styles={{ body: { padding: '12px 16px' } }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileImageOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  <Text strong>
                    {uploadedFiles.length} {intl.formatMessage({ id: 'gallery.photos' }) || 'Photos'} 
                    {' '}({(calculateTotalFileSize() / (1024 * 1024)).toFixed(2)} MB)
                  </Text>
                </div>
                {isFileListCollapsed && (
                  <Text type="secondary" style={{ fontSize: '12px', marginLeft: 24 }}>
                    {uploadedFiles.slice(0, 3).map(file => file.name).join(', ')}
                    {uploadedFiles.length > 3 ? `, ...${uploadedFiles.length - 3} more` : ''}
                  </Text>
                )}
              </div>
              <Button 
                type="text" 
                icon={isFileListCollapsed ? <DownOutlined /> : <UpOutlined />} 
                onClick={toggleFileListCollapse}
                size="small"
              >
                {isFileListCollapsed 
                  ? (intl.formatMessage({ id: 'gallery.showFiles' }) || 'Show Files') 
                  : (intl.formatMessage({ id: 'gallery.hideFiles' }) || 'Hide Files')}
              </Button>
            </div>
          </Card>
        )}
        
        <Dragger
          multiple
          beforeUpload={beforeUpload}
          onChange={handleFileChange}
          customRequest={({ onSuccess }) => {
            if (onSuccess) onSuccess("ok");
          }}
          showUploadList={!isFileListCollapsed}
          disabled={isUploading || getRemainingPhotoQuota() <= 0}
          accept="image/jpeg,image/png"
          maxCount={getRemainingPhotoQuota()}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {getRemainingPhotoQuota() > 0 
              ? (intl.formatMessage({ id: 'gallery.uploadDragText' }) || 'Click or drag files to this area to upload')
              : (intl.formatMessage({ id: 'gallery.uploadLimitReached' }, { limit: maxPhotosPerGallery }) || `You've reached the limit of ${maxPhotosPerGallery} photos for this gallery`)}
          </p>
          <p className="ant-upload-hint">
            {getRemainingPhotoQuota() > 0 
              ? (intl.formatMessage({ id: 'gallery.uploadHint' }) || 'Support for JPG, PNG. Maximum size: 10MB per file.')
              : (intl.formatMessage({ id: 'gallery.upgradeForMorePhotos' }) || 'Upgrade your plan to upload more photos')}
          </p>
        </Dragger>
        
        {isUploading && (
          <div style={{ marginTop: 16 }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <strong style={{ fontSize: '16px', color: '#1a2847' }}>
                {intl.formatMessage(
                  { id: 'gallery.uploadingProgress' },
                  { progress: uploadProgress }
                ) || `Uploading... ${uploadProgress}%`}
              </strong>
            </div>
            
            {/* Overall Progress bar */}
            <Progress 
              percent={uploadProgress} 
              strokeColor={{
                '0%': '#4169e1',
                '100%': '#52c41a',
              }}
              showInfo={false}
              style={{ marginBottom: 16 }}
            />
            
            {/* Individual file progress */}
            {uploadedFiles.length > 1 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: 16 }}>
                {uploadedFiles.map((file, index) => {
                  const fileProgress = fileUploadProgress.get(file.name) || 0;
                  const isFailed = failedUploads.has(file.name);
                  return (
                    <div key={file.uid || index} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text 
                          ellipsis 
                          style={{ 
                            flex: 1, 
                            marginRight: 8, 
                            fontSize: '12px',
                            color: isFailed ? '#ff4d4f' : undefined
                          }}
                        >
                          {file.name} {isFailed && '(Failed)'}
                        </Text>
                        <Text style={{ fontSize: '12px', color: isFailed ? '#ff4d4f' : undefined }}>
                          {fileProgress}%
                        </Text>
                      </div>
                      <Progress 
                        percent={fileProgress} 
                        size="small"
                        showInfo={false}
                        status={isFailed ? 'exception' : (fileProgress === 100 ? 'success' : 'active')}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              {uploadProgress === 100 ? (
                <div style={{ fontSize: 14, color: '#52c41a' }}>
                  <span role="img" aria-label="success">✓</span> Upload complete!
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#8c8c8c' }}>
                  Uploading {uploadedFiles.length} {uploadedFiles.length === 1 ? 'file' : 'files'}...
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Message shown when file list is collapsed and files are selected */}
        {isFileListCollapsed && uploadedFiles.length > 0 && !isUploading && (
          <div style={{ textAlign: 'center', padding: '12px', marginTop: 16, background: '#f0f8ff', borderRadius: 8 }}>
            <Text>
              {uploadedFiles.length} {intl.formatMessage({ id: 'gallery.photos' }) || 'Photos'} {intl.formatMessage({ id: 'gallery.selected' }) || 'selected'}. 
              <Button 
                type="link" 
                onClick={toggleFileListCollapse} 
                style={{ padding: '0 4px' }}
              >
                {intl.formatMessage({ id: 'gallery.showFiles' }) || 'Show Files'}
              </Button>
            </Text>
          </div>
        )}
      </Modal>
      
      {/* Delete All Photos Confirmation Modal */}
      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            {intl.formatMessage({ id: 'gallery.deleteAllPhotosConfirm.title' }) || 'Remove All Photos'}
          </span>
        }
        open={isDeleteAllModalVisible}
        onCancel={handleCancelDeleteAll}
        footer={[
          <Button key="cancel" onClick={handleCancelDeleteAll}>
            {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
          </Button>,
          <Button 
            key="delete" 
            type="primary" 
            danger 
            loading={isDeletingAll}
            onClick={handleDeleteAllPhotos}
          >
            {intl.formatMessage({ id: 'gallery.deleteAllPhotos' }) || 'Remove All Photos'}
          </Button>
        ]}
      >
        <p>{intl.formatMessage({ id: 'gallery.deleteAllPhotosConfirm.content' }) || 'This will permanently delete ALL photos in this gallery. This action cannot be undone.'}</p>
        {gallery && (
          <p style={{ fontWeight: 'bold' }}>
            "{gallery.name}"
          </p>
        )}
      </Modal>

      {/* Upgrade Modal */}
      <Modal
        open={isUpgradeModalVisible}
        onCancel={handleCloseUpgradeModal}
        footer={null}
        closable={false}
      >
        <UpgradeModal 
          onClose={handleCloseUpgradeModal} 
          featureName={intl.formatMessage({ id: 'features.maxPhotosPerGallery.name' }) || 'Photos Per Gallery'} 
          unlockLevel={intl.formatMessage(
            { id: 'organisation.plan.professional' },
            { plan: 'Professional' }
          ) || 'Professional Plan'}
        />
      </Modal>
    </StyledContent>
  );
};

export default GalleryDetailPage;